import { useState } from "react";
import { useNavigate } from "react-router";
import { ShieldX, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/auth/AuthShell";

interface AccessDeniedPageProps {
  /** Optional override for the explanatory line. */
  description?: string;
}

/**
 * Shown when a signed-in account lacks the role for the panel. Reuses the
 * shared {@link AuthShell} (card on the left, brand stage on the right) so the
 * "wrong account" state feels like part of the auth flow rather than a bare
 * error. "Użyj innego konta" signs the user out and returns to /login.
 */
export default function AccessDeniedPage({
  description = "To konto nie ma wymaganych uprawnień do panelu administracyjnego.",
}: AccessDeniedPageProps) {
  const { signOut, session } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSwitchAccount = async () => {
    setLoading(true);
    try {
      await signOut();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const email = session?.user?.email;

  return (
    <AuthShell>
      {/* Brand mark — matches the login page */}
      <div className="mb-8 flex items-center gap-2.5">
        <span className="lf-mark-logo">
          <ShieldX className="h-[18px] w-[18px]" />
        </span>
        <div>
          <div className="font-brand text-lg font-bold text-pulso-primary">PulsO</div>
          <div className="-mt-0.5 text-[11.5px] text-pulso-muted">panel admina</div>
        </div>
      </div>

      <div className="mb-6">
        <h1 className="font-brand text-[28px] font-bold leading-tight tracking-tight text-foreground">
          Brak dostępu
        </h1>
        <p className="mt-1.5 text-sm leading-relaxed text-pulso-muted">{description}</p>
        {email && (
          <p className="mt-3 text-sm text-pulso-muted">
            Zalogowano jako <strong className="text-foreground">{email}</strong>.
          </p>
        )}
      </div>

      <Button
        type="button"
        className="h-11 w-full gap-2 bg-pulso-primary text-[15px] font-semibold hover:bg-pulso-primary/90"
        onClick={handleSwitchAccount}
        disabled={loading}
      >
        <LogOut className="h-4 w-4" />
        {loading ? "Wylogowywanie..." : "Użyj innego konta"}
      </Button>

      <div className="mt-6 border-t border-black/[0.06] pt-5 text-center">
        <span className="text-[12.5px] text-pulso-muted">
          Potrzebujesz dostępu? Skontaktuj się z administratorem.
        </span>
      </div>
    </AuthShell>
  );
}
